import { Box, Center, Flex } from "@chakra-ui/react";
import { CustomText } from "@root/components/common/components";
import useSidebar from "@root/hooks/useSidebar";
import { isRouteExist } from "@root/utils/helpers";
import { NavControl } from "./navControl";
import { SidebarHeader } from "./sidebarHeader";
import { StyledSidebarContainer, StyledSidebarSubBox } from "./style";
import { CustomPopup } from "@root/components/common/modals/CustomPopup";
import { LogoutPopupContent } from "@root/pages/components";
import { useGlobalContext } from "@root/context/global-context";
import { LanguageToggle } from "./languagetoggle";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { useSelector } from "react-redux";
import { useEffect } from "react";

export function Sidebar() {
  const {
    isDarkTheme,
    lg,
    sidebar,
    pathname,
    languages,
    menuItemsList,
    profileData,
    logoutPopup,
    setLogoutPopup,
    logoutHandler,
    t,
    isMenuOpen,
    setIsMenuOpen,
  } = useSidebar();
  const isLoggedInUser: any = useSelector(getLoggedInUser);
  const { isSidebarOpen, setIsSidebarOpen, languagesOptions, getUserData } =
    useGlobalContext();

  useEffect(() => {
    if (isLoggedInUser) {
      getUserData().catch((error: any) => {
        console.error("Failed to fetch user profile:", error);
      });
    }
  }, [isLoggedInUser?.id]);

  // Removed useEffect that was forcing sidebar open on mount
  // Sidebar state is now loaded from localStorage on app initialization

  const SidebarItem = ({ menu, index }: any) => {
    const isActive = isRouteExist(pathname, menu.routes, menu?.DRoutes);
    const IconComponent = menu.iconComponent;
    return (
      <Flex
        width={isSidebarOpen ? "100%" : "36px"}
        height="36px"
        borderRadius="8px"
        justifyContent={isSidebarOpen ? "flex-start" : "center"} // ✅ key line
        alignItems="center"
        paddingLeft={isSidebarOpen ? "12px" : "0px"}
        cursor="pointer"
        onClick={menu.onClick}
        bg={isActive ? "#1819b4" : isSidebarOpen ? "white" : "transparent"}
        color={isActive ? "white" : "#5C6670"}
        _hover={{
          bg: "#1819b4",
          color: "white",
        }}
        gap={isSidebarOpen ? "8px" : "0"}
        data-tour-item={`sidebar-menu-${index}`}
      >
        <Box
          width="20px"
          height="20px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <IconComponent color="currentColor" />
        </Box>
        {isSidebarOpen && (
          <CustomText
            fontSize={"14px"}
            fontWeight={400}
            color="inherit"
            text={menu.text}
            alignItems={"center"}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          />
        )}
      </Flex>
    );
  };

  return (
    <StyledSidebarContainer // main box 1
      width={isSidebarOpen ? "250px" : "80px"}
      p={"10px"}
      data-tour-item="dashboard-sidebar"
    >
      <StyledSidebarSubBox gap={"30px"}>
        <SidebarHeader
          isDarkTheme={isDarkTheme}
          lg={lg}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
        <Flex flexDir={"column"} gap={"8px"}>
          {sidebar?.map((menu: any, index: number) => {
            return <SidebarItem key={index} menu={menu} index={index} />;
          })}
        </Flex>
      </StyledSidebarSubBox>

      <Flex
        flexDir={"column"}
        gap={"16px"}
        justifyContent="center"
        alignItems="center"
      >
        <LanguageToggle
          languages={languagesOptions}
          isSidebarOpen={isSidebarOpen}
        />
        {isLoggedInUser && (
          <NavControl
            pathname={pathname}
            menuItemsList={menuItemsList}
            isDarkTheme={isDarkTheme}
            isOpen={isSidebarOpen}
            profileData={profileData}
            isMenuOpen={isMenuOpen}
            setIsMenuOpen={setIsMenuOpen}
            languagesOptions={languagesOptions}
          />
        )}
      </Flex>

      {/* Logout Popup */}
      <CustomPopup
        open={logoutPopup}
        onClose={() => setLogoutPopup(false)}
        title={t("preferences.signOutModalTitle")}
        children={
          <LogoutPopupContent
            onSignOut={logoutHandler}
            onCancel={() => setLogoutPopup(false)}
          />
        }
      />
    </StyledSidebarContainer>
  );
}
